import { Component, Input, OnInit, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { AppModeService } from 'src/app/angular-wallet-base/services/app-mode.service';
import { LanguageService } from 'src/app/angular-wallet-base/services/language.service';
import { ModalController } from '@ionic/angular';
import { SelectAssetModal, SelectTokenInfo } from '../../select-asset-modal/select-asset-modal.component';
import { OTC3Order } from 'src/app/global';
import { OneInchService } from 'src/app/angular-wallet-base/services/one-inch.service';
import { PinModalComponent } from 'src/app/components/pin-modal/pin-modal.component';
import { AddressWAVAX, AUTH_REASON } from 'src/app/angular-wallet-base/constants';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/angular-wallet-base/store/appState';
import { FetchOrdersRequestAction, FulfillOrderAction } from 'src/app/angular-wallet-base/actions/otc3.actions';
import { getOtc3MarketOrders, getOtc3OrdersFetchingState, getOtc3PendingOrderId } from 'src/app/angular-wallet-base/store/otc3';
import { getKeychain } from 'src/app/angular-wallet-base/store/wallet';
import { getTokenInfoExcludeHLAndPTArray } from 'src/app/angular-wallet-base/utils';
import BigNumber from 'bignumber.js';
import { constants } from 'ethers';

@Component({
  selector: 'app-market-orders',
  templateUrl: './market-orders.component.html',
  styleUrls: ['./market-orders.component.scss'],
})
export class MarketOrdersComponent implements OnInit {
  @Input() wallet: any;

  public isDesktopMode: boolean = true;
  public currentLanguage: string = 'en';
  public assetToSell: SelectTokenInfo = null;
  public assetToBuy: SelectTokenInfo = null;
  public marketOrders: OTC3Order[] = [];
  public filteredOrders: OTC3Order[] = [];
  public totalTokens: SelectTokenInfo[] = [];
  public pin: string = null;
  public isFetchingOrders: boolean = true;
  public pendingOrder: number = -1;

  constructor(
    public appMode: AppModeService,
    private languageService: LanguageService,
    private fb: FormBuilder,
    private oneInchService: OneInchService,
    protected store: Store<AppState>,
    private modalController: ModalController
  ) {
    this.subscribeToAppMode();
    this.initializeLanguage();
  }

  async ngOnInit() {
    this.store.select(getKeychain).subscribe(async (keychain) => {
      if (!keychain) {
        return;
      }
      this.totalTokens = getTokenInfoExcludeHLAndPTArray(keychain) as SelectTokenInfo[];
    });
    this.store.select(getOtc3OrdersFetchingState).subscribe((state) => {
      this.isFetchingOrders = state;
    });
    this.store.select(getOtc3MarketOrders).subscribe((orders) => {
      this.marketOrders = orders.map((order) => {
        const tokenIn = this.totalTokens.find(
          (token) =>
            token.assetGuid?.toLowerCase() ===
            (order?.info?.tokenIn.toLowerCase() !== constants.AddressZero ? order?.info?.tokenIn.toLowerCase() : AddressWAVAX)
        );
        const tokenOut = this.totalTokens.find(
          (token) =>
            token.assetGuid?.toLowerCase() ===
            (order?.info?.tokenOut.toLowerCase() !== constants.AddressZero ? order?.info?.tokenOut.toLowerCase() : AddressWAVAX)
        );
        return {
          orderIdx: (order.id as BigNumber).toNumber(),
          amountIn: this._toFloat(order.info?.amountIn, tokenIn?.precision ?? 18, 5),
          amountOut: this._toFloat(order.info?.amountOut, tokenOut?.precision ?? 18, 5),
          tokenIn,
          tokenOut,
        };
      });
    });
    this.store.select(getOtc3PendingOrderId).subscribe((state) => {
      this.pendingOrder = state;
    });
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (!changes['wallet']) return;

    this.store.dispatch(new FetchOrdersRequestAction({ wallet: this.wallet, orderType: 'market_order' }));
  }

  private _toFloat(input: bigint, decimals: number, precision: number) {
    return parseFloat((Number(input) / 10 ** decimals).toFixed(precision));
  }

  private subscribeToAppMode(): void {
    this.appMode.isDesktopMode.subscribe((mode) => {
      this.isDesktopMode = mode;
    });
  }

  private initializeLanguage(): void {
    const currentLanguage = this.languageService.getCurrentLanguage();
    this.currentLanguage = currentLanguage?.length > 0 ? currentLanguage[0].value : 'en';
  }

  // Select asset
  async selectAsset(action: string): Promise<void> {
    const modal = await this.createAssetSelectionModal();
    await modal.present();
    this.handleModalDismiss(modal, action);
  }

  private async createAssetSelectionModal() {
    const cssClass = this.isDesktopMode ? 'select-asset-modal-desktop' : 'select-asset-modal';
    return this.modalController.create({
      component: SelectAssetModal,
      componentProps: {
        actionType: 'trade',
        assetToTrade: this.assetToSell,
        tradeFor: this.assetToBuy,
        selectFor: 'assetToTrade',
        orderType: 'market', // Assuming 'market' orderType here
      },
      cssClass,
      initialBreakpoint: this.isDesktopMode ? undefined : 0.9,
      breakpoints: this.isDesktopMode ? undefined : [0, 0.6, 0.9, 1],
    });
  }

  private async handleModalDismiss(modal: HTMLIonModalElement, action: string): Promise<void> {
    const response = await modal.onDidDismiss();
    if (response?.data) {
      if (action === 'sell') {
        if (response.data !== this.assetToSell) {
          this.assetToBuy = null;
        }
        this.assetToSell = response.data;
      } else if (action === 'buy') {
        this.assetToBuy = response.data;
      }
    }
  }

  filterOrders() {
    this.store.dispatch(
      new FetchOrdersRequestAction({
        wallet: this.wallet,
        orderType: 'market_order',
        tokenIn: this.assetToBuy?.assetGuid ?? null,
        tokenOut: this.assetToSell?.assetGuid ?? null,
      })
    );
  }

  private async openPinModal(): Promise<void> {
    const modal = await this.modalController.create({
      component: PinModalComponent,
      backdropDismiss: false,
      cssClass: 'fullscreen-modal',
      componentProps: {
        reason: AUTH_REASON.OTC3_CONNECT_SIGN,
      },
      id: 'pinModal',
    });

    await modal.present();
    const { data } = await modal.onDidDismiss();
    this.pin = data.pin;
    this.wallet = await this.oneInchService.getWallet(this.pin);
  }

  async handleBuy(order: OTC3Order) {
    this.store.dispatch(
      new FulfillOrderAction({
        wallet: this.wallet,
        order,
      })
    );
  }

  refreshOrders() {
    this.store.dispatch(new FetchOrdersRequestAction({ wallet: this.wallet, orderType: 'market_order' }));
  }
}
